/* eslint-disable max-lines-per-function */
import { ArbeitsbereichStation } from '@/domain/mitarbeiter/features/rotationsplan/entities/Station';
import { useDeleteArbeitsbereichStation } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useDeleteArbeitsbereichStation/useDeleteArbeitsbereichStation';
import { toast } from '@/hooks/use-toast';
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Drawer,
  DrawerContent,
  DrawerTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@e2n/ui';

export function DeleteArbeitsbereichStation({
  station,
}: {
  station: ArbeitsbereichStation;
}) {
  const { id } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [deleteArbeitsbereichStation] = useDeleteArbeitsbereichStation();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const tooltipMessage = undefined;
  /* station.istAbgeschlossen
    ? t(
        'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteStation.stationAbgeschlossenTooltipMessage',
      )
    : station.istAktiv
      ? t(
          'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteStation.stationAktivTooltipMessage',
        )
      : undefined;*/

  async function handleDelete() {
    setIsSubmitting(true);
    const result = await deleteArbeitsbereichStation({
      arbeitsbereichStationId: station.id,
      fristId: station.frist ? station.frist.id : undefined,
    });

    if (
      result.error ||
      result.data?.mitarbeiter_deleteArbeitsbereichStation.error
    ) {
      toast({
        description: t(
          'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteStation.error',
        ),
      });
    } else {
      navigate(`/mitarbeiter/rotationsplan/${id}`);

      let message = t(
        'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteStation.success',
      );

      if (
        result.data.mitarbeiter_deleteArbeitsbereichStation.success.fristResult
          ?.success
      ) {
        message +=
          ' ' +
          t(
            'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteStation.fristDeleteSuccess',
          );
      }

      if (
        result.data.mitarbeiter_deleteArbeitsbereichStation.success.fristResult
          ?.error
      ) {
        message +=
          ' ' +
          t(
            'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteStation.fristDeleteError',
          );
      }

      toast({
        description: message,
      });
    }
    setIsSubmitting(false);
  }

  return (
    <>
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="hidden md:max-w-111 p-8 md:flex flex-col gap-6">
          <DialogTitle>
            {t(
              'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteStation.titel',
            )}
          </DialogTitle>
          <div className="text-sm text-neutrals-900">
            {t(
              'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteStation.subtitle',
            )}
            {station.frist &&
              ' ' +
                t(
                  'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteStation.fristHinweis',
                )}
          </div>
          <div className="flex items-center justify-end gap-4">
            <Button
              onClick={() => {
                setIsDialogOpen(false);
              }}
              variant="outline">
              {t(
                'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteStation.dismiss',
              )}
            </Button>
            <Button
              disabled={isSubmitting}
              variant="destructive"
              onClick={handleDelete}>
              {t(
                'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteStation.confirm',
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger className="hidden md:flex">
            <Button
              /*disabled={
                isSubmitting || station.istAktiv || station.istAbgeschlossen
              }*/
              disabled={isSubmitting}
              onClick={() => setIsDialogOpen(true)}
              variant="ghost-destructive"
              size="icon">
              <FontAwesomeIcon icon={faTrashCan} />
            </Button>
          </TooltipTrigger>
          {tooltipMessage && <TooltipContent>{tooltipMessage}</TooltipContent>}
        </Tooltip>
      </TooltipProvider>

      <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
        <DrawerTrigger className="flex md:hidden" asChild>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger className="flex md:hidden">
                <Button
                  onClick={() => setIsDrawerOpen(true)}
                  /*disabled={
                    isSubmitting || station.istAktiv || station.istAbgeschlossen
                  }*/
                  disabled={isSubmitting}
                  variant="ghost-destructive"
                  size="icon">
                  <FontAwesomeIcon icon={faTrashCan} />
                </Button>
              </TooltipTrigger>
              {tooltipMessage && (
                <TooltipContent className="max-w-40">
                  {tooltipMessage}
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </DrawerTrigger>
        <DrawerContent className="flex flex-col items-center gap-5 p-4 md:hidden">
          <div className="flex flex-col items-center text-center gap-2 text-sm">
            <div className="font-semibold">
              {t(
                'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteStation.titel',
              )}
            </div>
            <div>
              {t(
                'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteStation.subtitle',
              )}
            </div>
          </div>
          <div className="flex flex-col gap-3 items-center w-full">
            <Button
              className="w-full"
              onClick={() => {
                setIsDrawerOpen(false);
              }}
              variant="outline">
              {t(
                'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteStation.dismiss',
              )}
            </Button>
            <Button
              className="w-full"
              variant="destructive"
              disabled={isSubmitting}
              onClick={handleDelete}>
              {t(
                'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteStation.confirm',
              )}
            </Button>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}
